import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {P, PMarginTMd, H2, PNoMargin} from '@/typography';
import {ListTerms} from '@/ui/lists/list-icons';
import {
  withMarginBottomXs,
  withMarginBottomXl,
  withMarginBottomMd,
  withMarginBottomXxl
} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import useComplianceTabItems from '@/ui/tabs/tab-items/compliance-tabs';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob
} from '@/ui/legal/legal.helper';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import modernSlaveryStatement from '../messages/en/modernSlaveryStatement.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('modernSlaveryStatement.seo.title'),
    description: seoT('modernSlaveryStatement.seo.description'),
    canonical: seoT('modernSlaveryStatement.seo.canonical')
  };
};

const imgPath = '/static/images/';

const List = props => <ListTerms {...props} />;
const BoldSpan = withBoldWeight('span');
const MarginBottomXs = withMarginBottomXs('li');
const MarginedH2 = withMarginBottomMd(H2);
const DivMarginedBottomXl = withMarginBottomXl('div');
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const ModernSlaveryStatementPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('modernSlaveryStatement');
  const legalT = useTranslations('legal');
  const tabItems = useComplianceTabItems();

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab="modern-slavery"
        title={legalT('sectionTitles.compliance')}
        tabItems={tabItems}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="compliance"
          defaultValue="modern-slavery"
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router)}
        />
      </div>
    )
  });

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const ModernSlaveryStatementContent = () => (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <H2 className="heading-4">{t('sections.introduction.title')}</H2>
      {t.raw('sections.introduction.paragraphs').map(paragraph => (
        <PMarginTMd key={paragraph}>{paragraph}</PMarginTMd>
      ))}
      <H2 className="heading-4">{t('sections.businessStructure.title')}</H2>
      {t.raw('sections.businessStructure.paragraphs').map(paragraph => (
        <PMarginTMd key={paragraph}>{paragraph}</PMarginTMd>
      ))}
      <H2 className="heading-4">{t('sections.approach.title')}</H2>
      <PMarginTMd>{t('sections.approach.intro')}</PMarginTMd>
      {t.raw('sections.approach.policies').map(policy => (
        <P key={policy.label}>
          <BoldSpan>{policy.label}</BoldSpan>: {policy.text}
        </P>
      ))}
      <H2 className="heading-4">{t('sections.supplyChain.title')}</H2>
      {t.raw('sections.supplyChain.paragraphs').map(paragraph => (
        <PMarginTMd key={paragraph}>{paragraph}</PMarginTMd>
      ))}
      <H2 className="heading-4">{t('sections.training.title')}</H2>
      <List>
        {t.raw('sections.training.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <H2 className="heading-4">{t('sections.furtherSteps.title')}</H2>
      <PMarginTMd>{t('sections.furtherSteps.paragraph')}</PMarginTMd>
      <H2 className="heading-4">{t('sections.approval.title')}</H2>
      <PMarginTMd>{t('sections.approval.paragraph')}</PMarginTMd>
      <ul>
        <MarginBottomXs>
          <PictureWrapper
            imgPath={imgPath}
            imageUrl="Signature-Nikos"
            imageTypeFallback="png"
            alt={t('sections.approval.signature.imageAlt')}
          />
        </MarginBottomXs>
        <MarginBottomXs>
          <BoldSpan>{t('sections.approval.signature.name')}</BoldSpan>
        </MarginBottomXs>
        <MarginBottomXs>
          <PNoMargin>{t('sections.approval.signature.role')}</PNoMargin>
        </MarginBottomXs>
      </ul>
    </>
  );

  return (
    <>
      <LegalHero title={legalT('sectionTitles.compliance')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.compliance')}
                activeTab={tabItems.find(item => item.value === 'modern-slavery').text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <ModernSlaveryStatementContent />
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(ModernSlaveryStatementPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, modernSlaveryStatement};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
